import { useState, useCallback, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, X, type LucideIcon } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';
import { useResponsive } from '../../hooks/useResponsive';

type InfoTooltipSize = 3 | 3.5 | 4 | 5;

/**
 * The visible glyph, per accepted `size`. Spelled out rather than interpolated
 * (`h-${size}`): Tailwind extracts classes by scanning source text, so a template
 * literal only ever works by accident — because some unrelated file happens to
 * ship the same class.
 */
const ICON_CLASSES: Record<InfoTooltipSize, string> = {
  3: 'h-3 w-3',
  3.5: 'h-3.5 w-3.5',
  4: 'h-4 w-4',
  5: 'h-5 w-5',
};

/**
 * Coarse-pointer tap target (44px), reserved as real layout — never bled.
 *
 * Same contract as `Checkbox` and `Switch`, and sharper here than on either: on a
 * touch device this trigger is a `Popover` trigger, because hover does not exist —
 * tapping is the ONLY way to read the help text. The control whose entire purpose
 * on mobile is to be tapped was drawn at 12-14px.
 *
 *  - `touch:m-[Npx]` reserves N px on every side, growing the trigger's own layout
 *    box to 44x44. Beside a form label or a section title, a touch device simply
 *    gets a slightly taller line and a wider gap.
 *  - `touch:after:-inset-[Npx]` grows the hit area to fill exactly that
 *    reservation, and no further.
 *
 * The two numbers are EQUAL here, unlike `Checkbox` (14 vs. 15) and `Switch`
 * (10 vs. 12), and for a reason worth stating: an absolutely-positioned `::after`
 * is offset from its container's PADDING box, so on those bordered controls the
 * inset has to clear the border first. This trigger is `border-0 p-0`, so padding
 * box == border box and the inset reaches exactly as far as it says. Add a border
 * or padding to the trigger and every number below has to move with it.
 *
 * N varies with `size` because the box does: the reservation is `(44 - box) / 2`,
 * so 12px of glyph reserves 16 a side and 20px of glyph reserves 12. A single
 * fixed inset would be right for exactly one caller.
 *
 * An overlay WITHOUT the matching margin is the banned alternative: an
 * `inline-flex` trigger sits beside labels and inside dense rows, so an unreserved
 * hit area spills onto whatever is next to it and a tap lands on the neighbour.
 * `__tests__/InfoTooltip.test.tsx` derives the target size from the glyph class and
 * both spacing numbers, for every accepted size, and goes red if any of them drifts.
 */
const TOUCH_TARGET_CLASSES: Record<InfoTooltipSize, string> = {
  3: 'touch:m-[16px] touch:after:-inset-[16px]',
  3.5: 'touch:m-[15px] touch:after:-inset-[15px]',
  4: 'touch:m-[14px] touch:after:-inset-[14px]',
  5: 'touch:m-[12px] touch:after:-inset-[12px]',
};

/** Makes the reserved box an actual hit area; the reach itself is per-size above. */
const TOUCH_TARGET_OVERLAY =
  "relative touch:after:absolute touch:after:content-['']";

interface InfoTooltipProps {
  /** The tooltip content to display. Usually a translated string; accepts a node
   *  so a sentence can carry an isolated token (e.g. a `<Trans>` with an
   *  `<LtrToken>` around an ID), which a plain string cannot express.
   *
   *  Rendered with NO `dir` of its own, so it takes the document direction —
   *  which is the correct base for our own translated copy. Isolate a foreign
   *  token inside it (`<LtrToken>` for an ID or a number, `<BidiText>` for an
   *  entity name); do not reach for `dir="auto"`, whose first-strong rule flips
   *  the whole sentence on a leading brand name (WI cf3fc9a4). */
  content: ReactNode;
  /** Additional className for the trigger button. Do not give it padding, a border
   *  or a transform — all three move the coarse-pointer tap target off 44px (see
   *  `TOUCH_TARGET_CLASSES`). */
  className?: string;
  /** Icon size - defaults to 3.5 (h-3.5 w-3.5) */
  size?: InfoTooltipSize;
  /** Side of the tooltip - defaults to 'right' for better form layout */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Accessible label for the trigger button (defaults to "More information"). */
  ariaLabel?: string;
  /** The trigger glyph. Defaults to `Info`.
   *
   *  For a hint whose SUBJECT has its own established glyph, so the icon carries
   *  the meaning and one control does the work of two — a padlock explaining why
   *  a value is read-only, rather than a padlock sitting next to an (i). Pass the
   *  component, not an element: this owns the sizing, and a caller-sized element
   *  would silently break the coarse-pointer target arithmetic above.
   *
   *  Keep it to a glyph that reads as "here is why". It must NOT be one that
   *  reads as an action — this trigger explains, it never mutates. */
  icon?: LucideIcon;
}

/**
 * A small info icon that displays contextual help.
 *
 * - **Desktop**: Tooltip on hover; click pins it open as a popover
 *   (close via X button or clicking outside)
 * - **Mobile/touch**: Popover on tap (since hover isn't available)
 *
 * Use this next to section titles, form labels, or anywhere brief
 * explanatory text helps the user understand a control or concept.
 */
export function InfoTooltip({
  content,
  className,
  size = 3.5,
  side = 'right',
  ariaLabel,
  icon: Glyph = Info,
}: InfoTooltipProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const sizeClass = ICON_CLASSES[size];

  const [pinned, setPinned] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  // Suppress tooltip flash when closing popover
  const suppressUntilLeaveRef = useRef(false);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPinned(true);
    setTooltipOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setPinned(false);
    suppressUntilLeaveRef.current = true;
  }, []);

  const handleTooltipOpenChange = useCallback((open: boolean) => {
    if (pinned || suppressUntilLeaveRef.current) {
      setTooltipOpen(false);
      return;
    }
    setTooltipOpen(open);
  }, [pinned]);

  const icon = (
    <button
      type="button"
      aria-label={ariaLabel ?? t('app.moreInformation')}
      className={cn(
        'inline-flex items-center justify-center cursor-help bg-transparent border-0 p-0 text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm',
        TOUCH_TARGET_OVERLAY,
        TOUCH_TARGET_CLASSES[size],
        className
      )}
      onClick={!isMobile ? handleClick : undefined}
      onPointerLeave={!isMobile ? () => { suppressUntilLeaveRef.current = false; } : undefined}
    >
      <Glyph className={cn(sizeClass, 'text-muted-foreground')} />
    </button>
  );

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>{icon}</PopoverTrigger>
        <PopoverContent
          side={side}
          collisionPadding={16}
          className="max-w-[280px] p-3 text-xs"
        >
          <p>{content}</p>
        </PopoverContent>
      </Popover>
    );
  }

  // Desktop: hover tooltip + click-to-pin popover
  return (
    <TooltipProvider>
      <Popover open={pinned} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <Tooltip delayDuration={300} open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>{icon}</TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent
            side={side}
            collisionPadding={16}
            className="max-w-[280px] text-wrap text-xs"
          >
            <p>{content}</p>
          </TooltipContent>
        </Tooltip>
        {pinned && (
          <PopoverContent
            side={side}
            collisionPadding={16}
            className="max-w-[280px] p-3 text-xs relative"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-1.5 end-1.5 p-0.5 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t('app.close')}
            >
              <X className="h-3 w-3" />
            </button>
            <p className="pe-4">{content}</p>
          </PopoverContent>
        )}
      </Popover>
    </TooltipProvider>
  );
}
